/**
 * ChatHistoryManager - Manages chat history viewing and management interface
 *
 * This module provides:
 * - Chat history viewing interface
 * - Session summaries and analytics
 * - History filtering and search
 * - Data management controls
 */

import { ChatStorageManager } from "./ChatStorageManager.js";

export class ChatHistoryManager {
    constructor() {
        this.chatStorageManager = new ChatStorageManager();
        this.historyPanel = null;
        this.isInitialized = false;
        this.currentHistoryData = [];
        this.filteredHistoryData = [];
        this.searchQuery = "";
        this.searchFilters = {
            character: "",
            dateRange: "",
        };

        this.initialize();
    }

    /**
     * Initialize the chat history manager
     */
    initialize() {
        if (document.readyState === "loading") {
            document.addEventListener("DOMContentLoaded", () =>
                this.setupInterface(),
            );
        } else {
            this.setupInterface();
        }
    }

    /**
     * Set up the chat history interface
     */
    setupInterface() {
        this.createHistoryPanel();
        this.setupEventListeners();
        this.isInitialized = true;
        console.log("ChatHistoryManager: Initialized successfully");

        // Check for pending chat restore after model switch
        this.checkPendingChatRestore();
    }

    /**
     * Check if there's a pending chat conversation to restore after model load
     */
    checkPendingChatRestore() {
        const pendingRestore = sessionStorage.getItem("pendingChatRestore");
        if (pendingRestore) {
            try {
                const { characterName, userMessage, characterResponse } =
                    JSON.parse(pendingRestore);
                console.log(
                    "ChatHistoryManager: Found pending chat restore for:",
                    characterName,
                );

                // Clear the pending restore
                sessionStorage.removeItem("pendingChatRestore");

                // Wait for chatController to be available, then restore
                this.waitForChatControllerAndRestore(
                    characterName,
                    userMessage,
                    characterResponse,
                );
            } catch (e) {
                console.error(
                    "ChatHistoryManager: Error restoring pending chat:",
                    e,
                );
                sessionStorage.removeItem("pendingChatRestore");
            }
        }
    }

    /**
     * Wait for chatController to be ready, then restore conversation
     */
    waitForChatControllerAndRestore(
        characterName,
        userMessage,
        characterResponse,
        attempts = 0,
    ) {
        const maxAttempts = 20;
        const checkInterval = 500; // 500ms between checks

        if (window.chatController && window.chatController.addMessageToChat) {
            console.log(
                "ChatHistoryManager: chatController ready, restoring conversation",
            );
            this.restoreConversationToChat(
                characterName,
                userMessage,
                characterResponse,
            );
        } else if (attempts < maxAttempts) {
            console.log(
                `ChatHistoryManager: Waiting for chatController (attempt ${attempts + 1}/${maxAttempts})`,
            );
            setTimeout(() => {
                this.waitForChatControllerAndRestore(
                    characterName,
                    userMessage,
                    characterResponse,
                    attempts + 1,
                );
            }, checkInterval);
        } else {
            console.error(
                "ChatHistoryManager: chatController not available after max attempts",
            );
        }
    }

    /**
     * Create the chat history panel HTML structure
     */
    createHistoryPanel() {
        // Create the history panel container
        const historyPanelHTML = `
            <div id="chat-history-panel" class="chat-history-panel d-none">
                <div class="history-header" style="padding: 15px; position: relative;">
                    <span>Chat History</span>
                    <div id="close-history-panel" class="btn rounded-circle d-flex align-items-center justify-content-center" 
                         style="width: 40px; height: 40px; background-color: rgba(58, 65, 73, 0.5); position: absolute; top: 50%; right: 15px; transform: translateY(-50%); cursor: pointer; box-shadow: 0 2px 8px rgba(0,0,0,1);">
                        <i class="fas fa-times" style="font-size: 20px;"></i>
                    </div>
                </div>
                
                <div class="history-search" style="padding: 20px 15px 15px 15px;">
                    <div class="search-container" style="position: relative; margin-bottom: 10px;">
                        <input type="text" id="search-conversations" class="form-control search-input" autocomplete="off"
                               placeholder="Search conversations..." 
                               style="padding-left: 35px; border-radius: 20px;">
                        <i class="fas fa-search" style="position: absolute; left: 12px; top: 50%; transform: translateY(-50%);"></i>
                        <button id="clear-search" class="btn btn-sm" style="position: absolute; right: 8px; top: 50%; transform: translateY(-50%); background: none; border: none; display: none;">
                            <i class="fas fa-times"></i>
                        </button>
                    </div>
                    
                    <div class="search-filters" style="display: flex; gap: 8px; flex-wrap: wrap;">
                        <select id="filter-character" class="form-select form-select-sm filter-select" style="border-radius: 15px; flex: 1; min-width: 120px;">
                            <option value="">All Characters</option>
                        </select>
                        

                        
                        <select id="filter-date" class="form-select form-select-sm filter-select" style="border-radius: 15px; flex: 1; min-width: 100px;">
                            <option value="">All Dates</option>
                            <option value="today">Today</option>
                            <option value="week">This Week</option>
                            <option value="month">This Month</option>
                        </select>
                    </div>
                    
                    <div id="search-results-info" class="search-info" style="margin-top: 10px; font-size: 0.85rem; display: none;">
                        <span id="results-count">0</span> conversations found
                    </div>
                </div>


                
                <div class="history-content">
                    <div id="history-loading" class="text-center p-3 d-none">
                        <div class="spinner-border spinner-border-sm" role="status">
                            <span class="visually-hidden">Loading...</span>
                        </div>
                        <div class="mt-2">Loading chat history...</div>
                    </div>
                    
                    <div id="history-list" class="history-list"></div>
                    
                    <div id="history-empty" class="text-center p-4 d-none">
                        <i class="fas fa-comments fa-3x text-muted mb-3"></i>
                        <h6 class="text-muted">No chat history found</h6>
                        <p class="text-muted small">Start chatting with characters to see your conversation history here.</p>
                    </div>
                    
                    <div id="history-auth-required" class="text-center p-4 d-none">
                        <i class="fas fa-lock fa-3x text-muted mb-3"></i>
                        <h6 class="text-muted">Authentication Required</h6>
                        <p class="text-muted small">Please sign in to view your chat history.</p>
                    </div>
                </div>
                
                <!-- Custom Confirmation Dialog -->
                <div id="switch-character-confirm" class="switch-confirm-overlay d-none">
                    <div class="switch-confirm-dialog">
                        <div class="switch-confirm-icon">
                            <i class="fas fa-exchange-alt"></i>
                        </div>
                        <h5 class="switch-confirm-title">Switch Character</h5>
                        <p class="switch-confirm-message">
                            To continue this conversation, you need to load <strong id="confirm-character-name"></strong>.
                        </p>
                        <div class="switch-confirm-actions">
                            <button id="confirm-switch-yes" class="switch-confirm-btn switch-confirm-yes">
                                <i class="fas fa-check"></i> Yes, switch
                            </button>
                            <button id="confirm-switch-cancel" class="switch-confirm-btn switch-confirm-cancel">
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        `;

        // Add the panel to the body
        document.body.insertAdjacentHTML("beforeend", historyPanelHTML);
        this.historyPanel = document.getElementById("chat-history-panel");

        // Add CSS styles
        this.addHistoryPanelStyles();
    }

    /**
     * Add CSS styles for the history panel
     */
    addHistoryPanelStyles() {
        const styles = `
            <style>
                /* Light Mode Styles (default) */
                .chat-history-panel {
                    position: fixed;
                    top: 0;
                    left: -450px;
                    width: 40vw;
                    min-width: 400px;
                    max-width: 400px;
                    height: 100vh;
                    background: white;
                    backdrop-filter: blur(15px);
                    -webkit-backdrop-filter: blur(15px);
                    border: 1px solid rgba(0, 0, 0, 0.1);
                    border-top-right-radius: 15px;
                    border-bottom-right-radius: 15px;
                    color: black;
                    z-index: 1050;
                    overflow: hidden;
                    box-shadow: 2px 0 10px rgba(0,0,0,0.3);
                    display: flex;
                    flex-direction: column;
                    /* Ultra-smooth animations */
                    transition: all 0.6s cubic-bezier(0.16, 1, 0.3, 1);
                    transform: translateX(-100%);
                    opacity: 0;
                    visibility: hidden;
                    will-change: transform, opacity;
                }

                /* Light Mode Panel - explicit */
                body.light-mode .chat-history-panel {
                    background: white;
                    color: black;
                    border: 1px solid rgba(0, 0, 0, 0.1);
                }

                /* Dark Mode Panel */
                body.dark-mode .chat-history-panel {
                    background: black;
                    color: white;
                    border: 1px solid rgba(255, 255, 255, 0.1);
                }

                .chat-history-panel.visible {
                    left: 0;
                    transform: translateX(0);
                    opacity: 1;
                    visibility: visible;
                }

                .chat-history-panel.opening {
                    animation: slideInFromLeft 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards;
                }

                .chat-history-panel.closing {
                    animation: slideOutToLeft 1.2s cubic-bezier(0.16, 1, 0.3, 1) forwards;
                }

                @keyframes slideInFromLeft {
                    0% {
                        transform: translateX(-100%) scale(0.95);
                        opacity: 0;
                        visibility: hidden;
                    }
                    50% {
                        opacity: 0.7;
                    }
                    100% {
                        transform: translateX(0) scale(1);
                        opacity: 1;
                        visibility: visible;
                    }
                }

                @keyframes slideOutToLeft {
                    0% {
                        transform: translateX(0) scale(1);
                        opacity: 1;
                        visibility: visible;
                    }
                    50% {
                        opacity: 0.7;
                    }
                    100% {
                        transform: translateX(-100%) scale(0.95);
                        opacity: 0;
                        visibility: hidden;
                    }
                }

                /* Light Mode Header */
                .history-header {
                    padding: 15px;
                    color: black;
                    border-bottom: 1px solid rgba(0, 0, 0, 0.15);
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    user-select: none;
                    background: transparent;
                    text-align: center;
                }

                .history-header span {
                    font-size: 1.2rem;
                    color: black;
                    font-weight: 500;
                    letter-spacing: 0.5px;
                }

                /* Light Mode Header - explicit */
                body.light-mode .history-header {
                    color: black;
                    border-bottom: 1px solid rgba(0, 0, 0, 0.15);
                }

                body.light-mode .history-header span {
                    color: black;
                }

                /* Dark Mode Header */
                body.dark-mode .history-header {
                    color: white;
                    border-bottom: 1px solid rgba(255, 255, 255, 0.15);
                }

                body.dark-mode .history-header span {
                    color: white;
                }

                /* Close button icon color */
                #close-history-panel i {
                    color: white;
                }


                .history-content {
                    flex: 1;
                    overflow-y: auto;
                    overflow-x: hidden;
                    scrollbar-width: none; /* Firefox */
                    -ms-overflow-style: none; /* Internet Explorer 10+ */
                }

                .history-content::-webkit-scrollbar {
                    display: none; /* Chrome, Safari, Edge */
                }

                .history-list {
                    padding: 1rem;
                    padding-bottom: 2rem; /* Extra padding at bottom for better scrolling */
                }

                /* Light Mode History Item */
                .history-item {
                    background: rgba(0, 0, 0, 0.05);
                    border-radius: 12px;
                    border: 1px solid rgba(0, 0, 0, 0.15);                   
                    padding: 1rem;
                    margin-bottom: 1rem;
                    cursor: pointer;
                    transition: all 0.2s ease;
                }

                .history-item:hover {
                    background: rgba(0, 0, 0, 0.1);
                    transform: translateY(-1px);
                }

                /* Light Mode History Item - explicit */
                body.light-mode .history-item {
                    background: rgba(0, 0, 0, 0.05);
                    border: 1px solid rgba(0, 0, 0, 0.15);
                }

                body.light-mode .history-item:hover {
                    background: rgba(0, 0, 0, 0.1);
                }

                /* Dark Mode History Item */
                body.dark-mode .history-item {
                    background: rgba(255, 255, 255, 0.1);
                    border: 1px solid rgba(255, 255, 255, 0.15);
                }

                body.dark-mode .history-item:hover {
                    background: rgba(255, 255, 255, 0.2);
                }

                .history-item-header {
                    display: flex;
                    justify-content: flex-start;
                    align-items: flex-start;
                    flex-direction: column;
                    margin-bottom: 0.5rem;
                }

                /* Light Mode Text Colors */
                .character-name {
                    font-size: 0.8rem;
                    color: black;
                    text-align: left;
                }

                .chat-date {
                    font-size: 0.6rem;
                    opacity: 0.7;
                    color: black;
                }

                .chat-preview {
                    font-size: 0.9rem;
                    opacity: 0.9;
                    line-height: 1.3;
                    color: black;
                }

                .user-message {
                    color: white;
                    background: rgba(58, 65, 73, 0.5);
                    padding: 8px 12px;
                    border-radius: 8px;
                    margin-bottom: 0.3rem;
                }

                .character-response {
                    margin-top: 0.3rem;
                    color: black;
                    display: flex;
                    flex-direction: column;
                    gap: 6px; /* pushes button a bit below the text */
                }

                /* Light Mode Text Colors - explicit */
                body.light-mode .character-name,
                body.light-mode .chat-date,
                body.light-mode .chat-preview,
                body.light-mode .character-response {
                    color: black;
                }

                /* User message keeps white text on both modes (has dark background) */
                body.light-mode .user-message,
                body.dark-mode .user-message {
                    color: white;
                }

                /* Dark Mode Text Colors */
                body.dark-mode .character-name,
                body.dark-mode .chat-date,
                body.dark-mode .chat-preview,
                body.dark-mode .character-response {
                    color: white;
                }

                /* Light Mode Tone Badge */
                .tone-badge {
                    display: inline-block;
                    background: rgba(58, 65, 73, 0.5);
                    color: white;
                    padding: 2px 6px;
                    border-radius: 10px;
                    font-size: 0.7rem;
                    margin-left: 0.5rem;
                }

                /* Expand Button - Same in both modes */
                
/* Smaller, tighter expand button */
.expand-btn {
  align-self: flex-start;     /* keep it snug, not stretching */
  display: inline-flex;
  padding: 4px 8px;           /* reduce background box size */
  line-height: 1;             /* tighter height */
  border-radius: 6px;         /* slightly smaller corner */
  font-size: 0.65rem;         /* smaller text */
  margin-top: 6px;            /* extra spacing below text */
  background: rgba(58, 65, 73, 0.3);
}

/* Optional: make hover feel lighter */
.expand-btn:hover {
  transform: translateY(-1px);
  background: rgba(58, 65, 73, 0.5);
}

                 background: rgba(58, 65, 73, 0.3);

                /* Get Back to Chat Button - Hover Reveal Style */
                .history-item {
                    position: relative;
                    overflow: hidden;
                }

                .get-back-to-chat-btn {
                    position: absolute;
                    right: -100%;
                    top: 10px;
                    background: rgba(58, 65, 73, 0.85);
                    border: none;
                    color: white;
                    padding: 8px 14px;
                    border-radius: 12px;
                    font-size: 0.75rem;
                    cursor: pointer;
                    transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    white-space: nowrap;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.3);
                    z-index: 10;
                }

                .history-item:hover .get-back-to-chat-btn {
                    right: 10px;
                }

                .get-back-to-chat-btn:hover {
                    background: rgba(58, 65, 73, 1);
                    transform: scale(1.05);
                }

                .get-back-to-chat-btn i {
                    font-size: 0.8rem;
                }

                /* Custom Confirmation Dialog Overlay */
                .switch-confirm-overlay {
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: rgba(0, 0, 0, 0.7);
                    backdrop-filter: blur(8px);
                    -webkit-backdrop-filter: blur(8px);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 100;
                    animation: fadeIn 0.3s ease;
                }

                .switch-confirm-dialog {
                    background: rgba(30, 30, 30, 0.95);
                    border-radius: 16px;
                    padding: 24px;
                    max-width: 320px;
                    width: 90%;
                    text-align: center;
                    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
                    border: 1px solid rgba(255, 255, 255, 0.1);
                    animation: scaleIn 0.3s cubic-bezier(0.16, 1, 0.3, 1);
                }

                @keyframes scaleIn {
                    from {
                        opacity: 0;
                        transform: scale(0.9);
                    }
                    to {
                        opacity: 1;
                        transform: scale(1);
                    }
                }

                .switch-confirm-icon {
                    width: 50px;
                    height: 50px;
                    background: rgba(58, 65, 73, 0.8);
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin: 0 auto 16px;
                }

                .switch-confirm-icon i {
                    font-size: 1.4rem;
                    color: white;
                }

                .switch-confirm-title {
                    color: white;
                    margin-bottom: 12px;
                    font-weight: 600;
                }

                .switch-confirm-message {
                    color: rgba(255, 255, 255, 0.8);
                    font-size: 0.9rem;
                    margin-bottom: 20px;
                    line-height: 1.5;
                }

                .switch-confirm-message strong {
                    color: white;
                }

                .switch-confirm-actions {
                    display: flex;
                    gap: 10px;
                    justify-content: center;
                }

                .switch-confirm-btn {
                    padding: 10px 20px;
                    border-radius: 8px;
                    font-size: 0.85rem;
                    font-weight: 500;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    border: none;
                }

                .switch-confirm-yes {
                    background: rgba(58, 65, 73, 0.9);
                    color: white;
                }

                .switch-confirm-yes:hover {
                    background: rgba(58, 65, 73, 1);
                    transform: translateY(-1px);
                }

                .switch-confirm-cancel {
                    background: transparent;
                    color: rgba(255, 255, 255, 0.7);
                    border: 1px solid rgba(255, 255, 255, 0.2);
                }

                .switch-confirm-cancel:hover {
                    background: rgba(255, 255, 255, 0.1);
                    color: white;
                }

                /* Light Mode Confirmation Dialog */
                body.light-mode .switch-confirm-overlay {
                    background: rgba(255, 255, 255, 0.7);
                }

                body.light-mode .switch-confirm-dialog {
                    background: rgba(255, 255, 255, 0.98);
                    border: 1px solid rgba(0, 0, 0, 0.1);
                    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
                }

                body.light-mode .switch-confirm-title {
                    color: black;
                }

                body.light-mode .switch-confirm-message {
                    color: rgba(0, 0, 0, 0.7);
                }

                body.light-mode .switch-confirm-message strong {
                    color: black;
                }

                body.light-mode .switch-confirm-cancel {
                    color: rgba(0, 0, 0, 0.6);
                    border: 1px solid rgba(0, 0, 0, 0.2);
                }

                body.light-mode .switch-confirm-cancel:hover {
                    background: rgba(0, 0, 0, 0.05);
                    color: black;
                }

                /* Light Mode Full Content */
                .full-content {
                    margin-top: 0.8rem;
                    padding-top: 0.8rem;
                    border-top: 1px solid rgba(0, 0, 0, 0.1);
                    animation: fadeIn 0.3s ease;
                }

                .full-user-message,
                .full-character-response {
                    margin-bottom: 0.5rem;
                    line-height: 1.4;
                    font-size: 0.9rem;
                }

                .full-user-message {
                    color: white;
                    background: rgba(58, 65, 73, 0.5);
                    padding: 8px 12px;
                    border-radius: 8px;
                }

                .full-character-response {
                    margin-bottom: 0;
                    color: black;
                }

                /* Light Mode Full Content - explicit */
                body.light-mode .full-content {
                    border-top: 1px solid rgba(0, 0, 0, 0.1);
                }

                body.light-mode .full-character-response {
                    color: black;
                }

                /* Dark Mode Full Content */
                body.dark-mode .full-content {
                    border-top: 1px solid rgba(255, 255, 255, 0.1);
                }

                body.dark-mode .full-character-response {
                    color: white;
                }

                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(-10px); }
                    to { opacity: 1; transform: translateY(0); }
                }

                .history-content {
                    flex: 1;
                    overflow-y: auto;
                    padding-bottom: 1rem;
                }

                /* Light Mode History Actions */
                .history-actions {
                    margin-top: auto;
                    padding: 1rem;
                    border-top: 1px solid rgba(0, 0, 0, 0.1);
                    background: rgba(0, 0, 0, 0.05);
                    flex-shrink: 0;
                }

                /* Light Mode History Actions - explicit */
                body.light-mode .history-actions {
                    border-top: 1px solid rgba(0, 0, 0, 0.1);
                    background: rgba(0, 0, 0, 0.05);
                }

                /* Dark Mode History Actions */
                body.dark-mode .history-actions {
                    border-top: 1px solid rgba(255, 255, 255, 0.1);
                    background: rgba(255, 255, 255, 0.05);
                }

                /* Light Mode Search Styles */
                .history-search {
                    border-bottom: 1px solid rgba(0, 0, 0, 0.1);
                }

                .chat-history-panel .search-input {
                    background: rgba(0, 0, 0, 0.05) !important;
                    border: 1px solid rgba(0, 0, 0, 0.2) !important;
                    color: black !important;
                }

                .chat-history-panel .search-input::placeholder {
                    color: rgba(0, 0, 0, 0.5) !important;
                }

                .chat-history-panel .search-input:focus {
                    outline: none !important;
                    border-color: rgba(0, 0, 0, 0.4) !important;
                    box-shadow: none !important;
                }

                .chat-history-panel .filter-select {
                    background: rgba(0, 0, 0, 0.05) !important;
                    border: 1px solid rgba(0, 0, 0, 0.2) !important;
                    color: black !important;
                }

                .chat-history-panel .filter-select:focus {
                    outline: none;
                    border-color: rgba(0, 0, 0, 0.4) !important;
                    box-shadow: 0 0 0 2px rgba(58, 65, 73, 0.3) !important;
                }

                .chat-history-panel .filter-select option {
                    background: white;
                    color: black;
                }

                /* Light Mode Search Styles - explicit */
                body.light-mode .history-search {
                    border-bottom: 1px solid rgba(0, 0, 0, 0.1);
                }

                body.light-mode .chat-history-panel .search-input {
                    background: rgba(0, 0, 0, 0.05) !important;
                    border: 1px solid rgba(0, 0, 0, 0.2) !important;
                    color: black !important;
                }

                body.light-mode .chat-history-panel .search-input::placeholder {
                    color: rgba(0, 0, 0, 0.5) !important;
                }

                body.light-mode .chat-history-panel .search-input:focus {
                    border-color: rgba(0, 0, 0, 0.4) !important;
                }

                body.light-mode .chat-history-panel .filter-select {
                    background: rgba(0, 0, 0, 0.05) !important;
                    border: 1px solid rgba(0, 0, 0, 0.2) !important;
                    color: black !important;
                }

                body.light-mode .chat-history-panel .filter-select:focus {
                    border-color: rgba(0, 0, 0, 0.4) !important;
                }

                body.light-mode .chat-history-panel .filter-select option {
                    background: white;
                    color: black;
                }

                /* Dark Mode Search Styles */
                body.dark-mode .history-search {
                    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
                }

                body.dark-mode .chat-history-panel .search-input {
                    background: rgba(255, 255, 255, 0.1) !important;
                    border: 1px solid rgba(255, 255, 255, 0.2) !important;
                    color: white !important;
                }

                body.dark-mode .chat-history-panel .search-input::placeholder {
                    color: rgba(255, 255, 255, 0.5) !important;
                }

                body.dark-mode .chat-history-panel .search-input:focus {
                    border-color: rgba(255, 255, 255, 0.4) !important;
                }

                body.dark-mode .chat-history-panel .filter-select {
                    background: rgba(255, 255, 255, 0.1) !important;
                    border: 1px solid rgba(255, 255, 255, 0.2) !important;
                    color: white !important;
                }

                body.dark-mode .chat-history-panel .filter-select:focus {
                    border-color: rgba(255, 255, 255, 0.4) !important;
                }

                body.dark-mode .chat-history-panel .filter-select option {
                    background: rgba(40, 40, 40, 0.95);
                    color: white;
                }

                /* Light Mode Search Info */
                body.light-mode #search-results-info {
                    color: rgba(0, 0, 0, 0.7);
                }

                /* Dark Mode Search Info */
                body.dark-mode #search-results-info {
                    color: rgba(255, 255, 255, 0.7);
                }

                /* Light Mode Search Icon Colors */
                body.light-mode .chat-history-panel .search-container i {
                    color: rgba(0, 0, 0, 0.6);
                }

                /* Dark Mode Search Icon Colors */
                body.dark-mode .chat-history-panel .search-container i {
                    color: rgba(255, 255, 255, 0.6);
                }

                /* Clear Search Button - Same in both modes */
                #clear-search {
                    background: rgba(58, 65, 73, 0.5) !important;
                    color: white !important;
                    border-radius: 50%;
                    width: 24px;
                    height: 24px;
                    padding: 0;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                #clear-search:hover {
                    background: rgba(58, 65, 73, 0.7) !important;
                }

                .search-highlight {
                    background-color: rgba(255, 255, 0, 0.3);
                    padding: 1px 2px;
                    border-radius: 2px;
                }

                .search-info {
                    font-style: italic;
                    padding: 5px 0;
                }

                /* Clear History Button - Same in both modes */
                #clear-history {
                    background: rgba(58, 65, 73, 0.5);
                    border-color: rgba(58, 65, 73, 0.7);
                    color: white;
                }

                #clear-history:hover {
                    background: rgba(58, 65, 73, 0.7);
                    border-color: rgba(58, 65, 73, 0.9);
                }

                /* Light Mode text-muted override */
                body.light-mode .chat-history-panel .text-muted {
                    color: rgba(0, 0, 0, 0.7) !important;
                }

                /* Dark Mode text-muted override */
                body.dark-mode .chat-history-panel .text-muted {
                    color: rgba(255, 255, 255, 0.7) !important;
                }

                /* Light Mode Empty/Auth text */
                body.light-mode .chat-history-panel #history-empty h6,
                body.light-mode .chat-history-panel #history-empty p,
                body.light-mode .chat-history-panel #history-auth-required h6,
                body.light-mode .chat-history-panel #history-auth-required p {
                    color: black !important;
                }

                /* Dark Mode Empty/Auth text */
                body.dark-mode .chat-history-panel #history-empty h6,
                body.dark-mode .chat-history-panel #history-empty p,
                body.dark-mode .chat-history-panel #history-auth-required h6,
                body.dark-mode .chat-history-panel #history-auth-required p {
                    color: white !important;
                }

                /* Light Mode Empty/Auth icons */
                body.light-mode .chat-history-panel #history-empty i,
                body.light-mode .chat-history-panel #history-auth-required i {
                    color: rgba(0, 0, 0, 0.5) !important;
                }


                /* Dark Mode Empty/Auth icons */
                body.dark-mode .chat-history-panel #history-empty i,
                body.dark-mode .chat-history-panel #history-auth-required i {
                    color: rgba(255, 255, 255, 0.5) !important;
                }

                /* Light Mode Loading text */
                body.light-mode .chat-history-panel #history-loading {
                    color: black !important;
                }

                /* Dark Mode Loading text */
                body.dark-mode .chat-history-panel #history-loading {
                    color: white !important;
                }

                /* Large Screens (1440px and above) */
                @media (min-width: 1440px) {
                    .chat-history-panel {
                        width: 40vw;
                        min-width: 450px;
                        max-width: 550px;
                        left: -550px;
                    }

                    @keyframes slideInFromLeft {
                        0% {
                            transform: translateX(-100%) scale(0.95);
                            opacity: 0;
                            visibility: hidden;
                        }
                        50% {
                            opacity: 0.7;
                        }
                        100% {
                            transform: translateX(0) scale(1);
                            opacity: 1;
                            visibility: visible;
                        }
                    }

                    @keyframes slideOutToLeft {
                        0% {
                            transform: translateX(0) scale(1);
                            opacity: 1;
                            visibility: visible;
                        }
                        50% {
                            opacity: 0.7;
                        }
                        100% {
                            transform: translateX(-100%) scale(0.95);
                            opacity: 0;
                            visibility: hidden;
                        }
                    }
                }

                /* Mobile Screens (767px and below) */
                @media (max-width: 767px) {
                    .chat-history-panel {
                        width: 85vw;
                        min-width: unset;
                        max-width: unset;
                        left: -85vw;
                    }

                    @keyframes slideInFromLeft {
                        0% {
                            transform: translateX(-100%) scale(0.95);
                            opacity: 0;
                            visibility: hidden;
                        }
                        50% {
                            opacity: 0.7;
                        }
                        100% {
                            transform: translateX(0) scale(1);
                            opacity: 1;
                            visibility: visible;
                        }
                    }

                    @keyframes slideOutToLeft {
                        0% {
                            transform: translateX(0) scale(1);
                            opacity: 1;
                            visibility: visible;
                        }
                        50% {
                            opacity: 0.7;
                        }
                        100% {
                            transform: translateX(-100%) scale(0.95);
                            opacity: 0;
                            visibility: hidden;
                        }
                    }
                }
            </style>
        `;

        document.head.insertAdjacentHTML("beforeend", styles);
    }

    /**
     * Set up event listeners for the history panel
     */
    setupEventListeners() {
        // Close panel button
        const closeButton = document.getElementById("close-history-panel");
        if (closeButton) {
            closeButton.addEventListener("click", () => this.hidePanel());
        }

        // Search functionality
        const searchInput = document.getElementById("search-conversations");
        const clearSearchButton = document.getElementById("clear-search");
        const characterFilter = document.getElementById("filter-character");
        const dateFilter = document.getElementById("filter-date");

        if (searchInput) {
            searchInput.addEventListener("input", (e) => {
                this.searchQuery = e.target.value.trim();
                this.updateSearchResults();
                this.toggleClearSearchButton();
            });
        }

        if (clearSearchButton) {
            clearSearchButton.addEventListener("click", () => {
                this.clearSearch();
            });
        }

        if (characterFilter) {
            characterFilter.addEventListener("change", (e) => {
                this.searchFilters.character = e.target.value;
                this.updateSearchResults();
            });
        }

        if (dateFilter) {
            dateFilter.addEventListener("change", (e) => {
                this.searchFilters.dateRange = e.target.value;
                this.updateSearchResults();
            });
        }

        // Action buttons
        const clearButton = document.getElementById("clear-history");

        if (clearButton) {
            clearButton.addEventListener("click", () =>
                this.confirmClearHistory(),
            );
        }

        // Note: Auto-close functionality removed per user request
        // Panel will only close when user clicks the close button
    }

    /**
     * Check if clicked element is a history button
     */
    isHistoryButton(element) {
        return (
            element.id === "history-button" ||
            element.closest("#history-button") ||
            element.classList.contains("history-trigger")
        );
    }

    /**
     * Retrieve user's chat history directly from Firestore using Firebase v9+ syntax
     * @returns {Promise<Array>} Array of chat history objects
     */
    async getUserChatHistoryFromFirestore() {
        if (!this.chatStorageManager.isStorageAvailable()) {
            console.warn(
                "ChatHistoryManager: User not authenticated, cannot retrieve chat history",
            );
            return [];
        }

        try {
            const currentUser = this.chatStorageManager.getCurrentUser();
            console.log(
                "ChatHistoryManager: Retrieving chat history for user:",
                currentUser.uid,
            );
            console.log("ChatHistoryManager: User email:", currentUser.email);

            // Import Firebase v9+ functions
            const { collection, query, where, orderBy, getDocs } = await import(
                "https://www.gstatic.com/firebasejs/10.7.1/firebase-firestore.js"
            );

            // Use the global Firebase database instance
            const db = window.firebaseDB;

            if (!db) {
                throw new Error("Firebase database not available");
            }

            console.log("ChatHistoryManager: Firebase DB available:", !!db);

            // Create query to get user's chat history
            const chatHistoryQuery = query(
                collection(db, "chatHistory"),
                where("userId", "==", currentUser.uid),
            );

            // Execute the query
            const querySnapshot = await getDocs(chatHistoryQuery);
            const chatHistory = [];

            querySnapshot.forEach((doc) => {
                const data = doc.data();
                chatHistory.push({
                    id: doc.id,
                    characterName: data.characterName,
                    userMessage: data.userMessage,
                    characterResponse: data.characterResponse,
                    detectedTone: data.detectedTone,
                    timestamp: data.timestamp.toDate
                        ? data.timestamp.toDate()
                        : new Date(data.timestamp),
                    createdAt: data.createdAt,
                    sessionId: data.sessionId,
                    userEmail: data.userEmail,
                });
            });

            // Sort by timestamp (newest first)
            chatHistory.sort((a, b) => b.timestamp - a.timestamp);

            console.log(
                "ChatHistoryManager: Retrieved",
                chatHistory.length,
                "chat conversations from Firestore",
            );
            return chatHistory;
        } catch (error) {
            console.error(
                "ChatHistoryManager: Error retrieving chat history from Firestore:",
                error,
            );
            console.error(
                "ChatHistoryManager: Full error details:",
                error.message,
                error.code,
            );

            // Try a simpler query without filtering if the main query fails
            try {
                console.log(
                    "ChatHistoryManager: Attempting fallback query without user filtering...",
                );
                const { collection, getDocs } = await import(
                    "https://www.gstatic.com/firebasejs/10.7.1/firebase-firestore.js"
                );
                const db = window.firebaseDB;

                const allChatsQuery = collection(db, "chatHistory");
                const querySnapshot = await getDocs(allChatsQuery);
                const allChats = [];

                querySnapshot.forEach((doc) => {
                    const data = doc.data();
                    // Filter for current user in JavaScript
                    if (data.userId === currentUser.uid) {
                        allChats.push({
                            id: doc.id,
                            characterName: data.characterName,
                            userMessage: data.userMessage,
                            characterResponse: data.characterResponse,
                            detectedTone: data.detectedTone,
                            timestamp: data.timestamp.toDate
                                ? data.timestamp.toDate()
                                : new Date(data.timestamp),
                            createdAt: data.createdAt,
                            sessionId: data.sessionId,
                            userEmail: data.userEmail,
                        });
                    }
                });

                // Sort by timestamp (newest first)
                allChats.sort((a, b) => b.timestamp - a.timestamp);

                console.log(
                    "ChatHistoryManager: Fallback query retrieved",
                    allChats.length,
                    "conversations",
                );
                return allChats;
            } catch (fallbackError) {
                console.error(
                    "ChatHistoryManager: Fallback query also failed:",
                    fallbackError,
                );
                return [];
            }
        }
    }

    /**
     * Show the history panel
     */
    async showPanel() {
        console.log("ChatHistoryManager: showPanel called");

        if (!this.historyPanel) {
            console.log(
                "ChatHistoryManager: historyPanel not found, panel may not be created yet",
            );
            return;
        }

        console.log("ChatHistoryManager: Starting panel opening animation");

        // Remove d-none and any closing classes
        this.historyPanel.classList.remove("d-none", "closing");

        // Force reflow to ensure classes are applied
        this.historyPanel.offsetHeight;

        // Add opening animation class and visible class together
        this.historyPanel.classList.add("opening", "visible");

        // After animation completes, remove opening class
        setTimeout(() => {
            this.historyPanel.classList.remove("opening");
        }, 600);

        console.log(
            "ChatHistoryManager: Panel classes after update:",
            this.historyPanel.className,
        );

        // Check authentication and load data
        if (this.chatStorageManager.isStorageAvailable()) {
            console.log("ChatHistoryManager: User authenticated, loading data");
            await this.loadHistoryData();
        } else {
            console.log(
                "ChatHistoryManager: User not authenticated, showing auth required",
            );
            this.showAuthRequired();
        }
    }

    /**
     * Hide the history panel
     */
    hidePanel() {
        if (this.historyPanel) {
            console.log("ChatHistoryManager: Starting panel closing animation");

            // Remove visible and opening classes, add closing
            this.historyPanel.classList.remove("visible", "opening");
            this.historyPanel.classList.add("closing");

            // After animation completes, hide the panel
            setTimeout(() => {
                this.historyPanel.classList.remove("closing");
                this.historyPanel.classList.add("d-none");
                console.log("ChatHistoryManager: Panel closed and hidden");
            }, 1200); // Match slower animation duration
        }
    }

    /**
     * Show authentication required message
     */
    showAuthRequired() {
        document.getElementById("history-list").classList.add("d-none");
        document.getElementById("history-empty").classList.add("d-none");
        document.getElementById("history-loading").classList.add("d-none");
        document
            .getElementById("history-auth-required")
            .classList.remove("d-none");
    }

    /**
     * Load chat history data
     */
    async loadHistoryData() {
        this.showLoading(true);

        try {
            // Get user's chat history directly from Firestore using Firebase v9+ syntax
            const history = await this.getUserChatHistoryFromFirestore();

            this.currentHistoryData = history;
            this.filteredHistoryData = history; // Initialize filtered data

            console.log(
                "ChatHistoryManager: About to display history data. Count:",
                history.length,
            );

            // Populate character filter dropdown
            this.populateCharacterFilter();

            this.displayHistoryData(history);
        } catch (error) {
            console.error("Error loading chat history:", error);
            this.showError("Failed to load chat history");
        } finally {
            this.showLoading(false);
        }
    }

    /**
     * Display history data in the panel
     */
    displayHistoryData(historyData) {
        console.log(
            "ChatHistoryManager: displayHistoryData called with",
            historyData.length,
            "items",
        );

        const historyList = document.getElementById("history-list");
        const emptyState = document.getElementById("history-empty");

        console.log(
            "ChatHistoryManager: historyList element found:",
            !!historyList,
        );
        console.log(
            "ChatHistoryManager: emptyState element found:",
            !!emptyState,
        );

        if (historyData.length === 0) {
            console.log(
                "ChatHistoryManager: No history data, showing empty state",
            );
            historyList?.classList.add("d-none");
            emptyState?.classList.remove("d-none");
            return;
        }

        console.log(
            "ChatHistoryManager: Showing history list with",
            historyData.length,
            "items",
        );
        emptyState?.classList.add("d-none");
        historyList?.classList.remove("d-none");

        const historyHTML = historyData
            .map((item) => this.createHistoryItemHTML(item))
            .join("");
        console.log(
            "ChatHistoryManager: Generated HTML length:",
            historyHTML.length,
        );
        console.log("ChatHistoryManager: First item preview:", historyData[0]);

        if (historyList) {
            historyList.innerHTML = historyHTML;
            console.log("ChatHistoryManager: HTML content set to historyList");
        }

        // Add click listeners to history items
        historyList
            ?.querySelectorAll(".history-item")
            .forEach((item, index) => {
                item.addEventListener("click", () =>
                    this.showHistoryItemDetail(historyData[index]),
                );
            });

        // Add click listeners to "Get Back to Chat" buttons
        historyList
            ?.querySelectorAll(".get-back-to-chat-btn")
            .forEach((btn) => {
                btn.addEventListener("click", (e) => {
                    e.stopPropagation(); // Prevent triggering parent history-item click
                    const characterName = btn.dataset.characterName;
                    const userMessage = btn.dataset.userMessage;
                    const characterResponse = btn.dataset.characterResponse;
                    this.handleGetBackToChat(
                        characterName,
                        userMessage,
                        characterResponse,
                    );
                });
            });

        console.log(
            "ChatHistoryManager: Click listeners added to history items",
        );
    }

    /**
     * Create HTML for a single history item
     */
    createHistoryItemHTML(item) {
        const date = new Date(item.timestamp).toLocaleDateString();
        const time = new Date(item.timestamp).toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
        });

        // Apply search highlighting if there's an active search
        const userMessage = this.searchQuery
            ? this.highlightSearchTerm(item.userMessage, this.searchQuery)
            : item.userMessage;

        const characterResponse = this.searchQuery
            ? this.highlightSearchTerm(item.characterResponse, this.searchQuery)
            : item.characterResponse;

        const userPreview =
            userMessage.length > 60
                ? userMessage.substring(0, 60) + "..."
                : userMessage;

        const responsePreview =
            characterResponse.length > 120
                ? characterResponse.substring(0, 120) + "..."
                : characterResponse;

        const hasLongResponse = item.characterResponse.length > 120;
        const expandButton = hasLongResponse
            ? `<button class="expand-btn" onclick="event.stopPropagation(); this.closest('.history-item').querySelector('.full-content').classList.toggle('d-none'); this.textContent = this.textContent === 'Show More' ? 'Show Less' : 'Show More';">Show More</button>`
            : "";

        return `
            <div class="history-item" data-id="${item.id}">
                <div class="history-item-header">
                    <span class="character-name">${item.characterName}</span>
                    <span class="chat-date">${date} ${time}</span>
                </div>
                <div class="chat-preview">
                    <div class="user-message">"${userPreview}"</div>
                    <div class="character-response">
                        ${item.characterName}: "${responsePreview}"
                        ${expandButton}
                    </div>
                    ${
                        hasLongResponse
                            ? `
                        <div class="full-content d-none">
                            <div class="full-user-message">
                                "${userMessage}"
                            </div>
                            <div class="full-character-response">
                                <strong>${item.characterName}:</strong> "${characterResponse}"
                            </div>
                        </div>
                    `
                            : ""
                    }
                    <button class="get-back-to-chat-btn" data-character-name="${item.characterName}" data-user-message="${this.escapeHtml(item.userMessage)}" data-character-response="${this.escapeHtml(item.characterResponse)}">
                        <i class="fas fa-arrow-right"></i>Continue
                    </button>
                </div>
            </div>
        `;
    }

    /**
     * Escape HTML special characters to prevent XSS
     */
    escapeHtml(text) {
        const div = document.createElement("div");
        div.textContent = text;
        return div.innerHTML;
    }

    /**
     * Handle "Get Back to Chat" button click
     */
    handleGetBackToChat(characterName, userMessage, characterResponse) {
        console.log(
            "ChatHistoryManager: Get Back to Chat clicked for:",
            characterName,
        );

        // Try to get characterData from window (set in main.js)
        const characterData = window.characterData;
        if (!characterData) {
            console.error("ChatHistoryManager: characterData not available");
            alert("Unable to load character data. Please refresh the page.");
            return;
        }

        // Find the character ID by iterating through characterData keys
        let characterModelId = null;
        for (const id in characterData) {
            if (
                characterData.hasOwnProperty(id) &&
                typeof characterData[id] === "object" &&
                characterData[id].name === characterName
            ) {
                characterModelId = id;
                break;
            }
        }

        if (!characterModelId) {
            console.error(
                "ChatHistoryManager: Character not found:",
                characterName,
            );
            alert(`Character "${characterName}" not found.`);
            return;
        }

        // Check if this model is currently loaded
        const currentModelId = window.ModelManager?.getCurrentModelId?.();
        console.log(
            "ChatHistoryManager: Character model ID:",
            characterModelId,
        );
        console.log("ChatHistoryManager: Current model ID:", currentModelId);

        if (currentModelId === characterModelId) {
            // Model is loaded - restore conversation and open chat
            console.log(
                "ChatHistoryManager: Model already loaded, restoring conversation",
            );
            this.restoreConversationToChat(
                characterName,
                userMessage,
                characterResponse,
            );
            this.hidePanel();
        } else {
            // Model is not loaded - show custom confirmation dialog
            console.log(
                "ChatHistoryManager: Model not loaded, showing confirmation dialog",
            );
            this.showSwitchConfirmDialog(
                characterName,
                characterModelId,
                userMessage,
                characterResponse,
            );
        }
    }

    /**
     * Show the custom switch character confirmation dialog
     */
    showSwitchConfirmDialog(
        characterName,
        characterModelId,
        userMessage,
        characterResponse,
    ) {
        const overlay = document.getElementById("switch-character-confirm");
        const characterNameEl = document.getElementById(
            "confirm-character-name",
        );
        const yesBtn = document.getElementById("confirm-switch-yes");
        const cancelBtn = document.getElementById("confirm-switch-cancel");

        if (!overlay || !characterNameEl) {
            console.error(
                "ChatHistoryManager: Confirmation dialog elements not found",
            );
            return;
        }

        // Set the character name in the message
        characterNameEl.textContent = characterName;

        // Show the overlay
        overlay.classList.remove("d-none");

        // Remove existing event listeners by cloning buttons
        const newYesBtn = yesBtn.cloneNode(true);
        const newCancelBtn = cancelBtn.cloneNode(true);
        yesBtn.parentNode.replaceChild(newYesBtn, yesBtn);
        cancelBtn.parentNode.replaceChild(newCancelBtn, cancelBtn);

        // Handle Yes click
        newYesBtn.addEventListener("click", () => {
            console.log(
                "ChatHistoryManager: User confirmed switch to",
                characterName,
            );

            // Store conversation to restore after model loads
            sessionStorage.setItem(
                "pendingChatRestore",
                JSON.stringify({
                    characterName,
                    userMessage,
                    characterResponse,
                }),
            );

            // Hide the dialog
            overlay.classList.add("d-none");

            // Load the character model (this will reload the page)
            if (window.ModelManager) {
                window.ModelManager.setCurrentModel(characterModelId, true);
            }
        });

        // Handle Cancel click
        newCancelBtn.addEventListener("click", () => {
            console.log("ChatHistoryManager: User cancelled switch");
            overlay.classList.add("d-none");
        });

        // Handle clicking outside the dialog
        overlay.addEventListener("click", (e) => {
            if (e.target === overlay) {
                overlay.classList.add("d-none");
            }
        });
    }

    /**
     * Restore conversation to the chat window
     */
    restoreConversationToChat(characterName, userMessage, characterResponse) {
        console.log("ChatHistoryManager: Restoring conversation to chat");

        // Get the chat controller
        const chatController = window.chatController;
        if (!chatController) {
            console.error("ChatHistoryManager: chatController not available");
            return;
        }

        // Clear existing chat history display
        const chatHistory = document.getElementById("chat-history");
        if (chatHistory) {
            chatHistory.innerHTML = "";
        }

        // Clear existing conversation history in the controller
        if (chatController.clearConversationHistory) {
            chatController.clearConversationHistory();
        }

        // Add the user message to display
        if (chatController.addMessageToChat) {
            chatController.addMessageToChat("", userMessage, "user-message");
        }

        // Add the character response to display
        if (chatController.addMessageToChat) {
            chatController.addMessageToChat(
                "",
                characterResponse,
                "bot-message",
            );
        }

        // IMPORTANT: Add messages to conversation history for AI context
        // This ensures the AI knows what was discussed when user sends next message
        if (chatController.addToConversationHistory) {
            chatController.addToConversationHistory("user", userMessage);
            chatController.addToConversationHistory(
                "assistant",
                characterResponse,
            );
            console.log(
                "ChatHistoryManager: Added conversation context for AI",
            );
        }

        // Make sure chat panel is visible
        const chatPanel = document.getElementById("chat-panel");
        if (chatPanel) {
            chatPanel.classList.remove("d-none");
            chatPanel.style.display = "block";
        }

        // Scroll to bottom
        if (chatHistory) {
            chatHistory.scrollTop = chatHistory.scrollHeight;
        }

        console.log(
            "ChatHistoryManager: Conversation restored to chat with AI context",
        );
    }

    /**
     * Show/hide loading state
     */
    showLoading(show) {
        const loading = document.getElementById("history-loading");
        const list = document.getElementById("history-list");
        const empty = document.getElementById("history-empty");
        const authRequired = document.getElementById("history-auth-required");

        if (show) {
            loading?.classList.remove("d-none");
            list?.classList.add("d-none");
            empty?.classList.add("d-none");
            authRequired?.classList.add("d-none");
        } else {
            loading?.classList.add("d-none");
        }
    }

    /**
     * Confirm and clear chat history
     */
    confirmClearHistory() {
        const character = this.currentFilter.character;
        const message = character
            ? `Are you sure you want to delete all chat history with ${character}?`
            : "Are you sure you want to delete all your chat history?";

        if (confirm(message)) {
            this.clearHistory(character);
        }
    }

    /**
     * Clear chat history
     */
    async clearHistory(characterName = null) {
        try {
            const success =
                await this.chatStorageManager.deleteChatHistory(characterName);
            if (success) {
                await this.loadHistoryData(); // Reload data
                alert("Chat history cleared successfully");
            } else {
                alert("Failed to clear chat history");
            }
        } catch (error) {
            console.error("Error clearing history:", error);
            alert("Failed to clear chat history");
        }
    }

    /**
     * Show error message
     */
    showError(message) {
        console.error("ChatHistoryManager:", message);
        // Could add toast notification here
    }

    /**
     * Show detailed view of a history item
     */
    showHistoryItemDetail(item) {
        // Could implement a detailed view modal
        console.log("Show detail for item:", item);
    }

    /**
     * Update search results based on current query and filters
     */
    updateSearchResults() {
        const hasActiveSearch =
            this.searchQuery ||
            this.searchFilters.character ||
            this.searchFilters.dateRange;

        if (!hasActiveSearch) {
            // No active search/filters, show all data
            this.filteredHistoryData = this.currentHistoryData;
        } else {
            // Apply search and filters
            this.filteredHistoryData = this.currentHistoryData.filter(
                (item) => {
                    return this.matchesSearchCriteria(item);
                },
            );
        }

        // Update display
        this.displayHistoryData(this.filteredHistoryData);
        this.updateSearchResultsInfo();
    }

    /**
     * Check if a history item matches current search criteria
     */
    matchesSearchCriteria(item) {
        // Text search in user message and character response
        if (this.searchQuery) {
            const searchText = this.searchQuery.toLowerCase();
            const userMessage = (item.userMessage || "").toLowerCase();
            const characterResponse = (
                item.characterResponse || ""
            ).toLowerCase();

            if (
                !userMessage.includes(searchText) &&
                !characterResponse.includes(searchText)
            ) {
                return false;
            }
        }

        // Character filter
        if (
            this.searchFilters.character &&
            item.characterName !== this.searchFilters.character
        ) {
            return false;
        }

        // Date filter
        if (this.searchFilters.dateRange) {
            if (
                !this.matchesDateRange(
                    item.timestamp,
                    this.searchFilters.dateRange,
                )
            ) {
                return false;
            }
        }

        return true;
    }

    /**
     * Check if a timestamp matches the specified date range
     */
    matchesDateRange(timestamp, range) {
        const itemDate = new Date(timestamp);
        const now = new Date();

        switch (range) {
            case "today":
                return itemDate.toDateString() === now.toDateString();

            case "week":
                const weekAgo = new Date(
                    now.getTime() - 7 * 24 * 60 * 60 * 1000,
                );
                return itemDate >= weekAgo;

            case "month":
                const monthAgo = new Date(
                    now.getTime() - 30 * 24 * 60 * 60 * 1000,
                );
                return itemDate >= monthAgo;

            default:
                return true;
        }
    }

    /**
     * Populate the character filter dropdown with available characters
     */
    populateCharacterFilter() {
        const characterFilter = document.getElementById("filter-character");
        if (!characterFilter) return;

        // Get unique character names from history data
        const characters = [
            ...new Set(
                this.currentHistoryData.map((item) => item.characterName),
            ),
        ];

        // Clear existing options except "All Characters"
        const defaultOption = characterFilter.querySelector('option[value=""]');
        characterFilter.innerHTML = "";
        if (defaultOption) {
            characterFilter.appendChild(defaultOption);
        } else {
            characterFilter.innerHTML =
                '<option value="">All Characters</option>';
        }

        // Add character options
        characters.forEach((character) => {
            if (character) {
                const option = document.createElement("option");
                option.value = character;
                option.textContent = character;
                characterFilter.appendChild(option);
            }
        });
    }

    /**
     * Clear search and reset filters
     */
    clearSearch() {
        // Clear search input
        const searchInput = document.getElementById("search-conversations");
        if (searchInput) {
            searchInput.value = "";
        }

        // Reset filters
        const characterFilter = document.getElementById("filter-character");
        const dateFilter = document.getElementById("filter-date");

        if (characterFilter) characterFilter.value = "";
        if (dateFilter) dateFilter.value = "";

        // Reset internal state
        this.searchQuery = "";
        this.searchFilters = {
            character: "",
            dateRange: "",
        };

        // Update results
        this.updateSearchResults();
        this.toggleClearSearchButton();
    }

    /**
     * Toggle visibility of clear search button
     */
    toggleClearSearchButton() {
        const clearButton = document.getElementById("clear-search");
        if (clearButton) {
            const hasSearch = this.searchQuery.length > 0;
            clearButton.style.display = hasSearch ? "block" : "none";
        }
    }

    /**
     * Update search results information display
     */
    updateSearchResultsInfo() {
        const resultsInfo = document.getElementById("search-results-info");
        const resultsCount = document.getElementById("results-count");

        if (resultsInfo && resultsCount) {
            const hasActiveSearch =
                this.searchQuery ||
                this.searchFilters.character ||
                this.searchFilters.tone ||
                this.searchFilters.dateRange;

            if (hasActiveSearch) {
                resultsCount.textContent = this.filteredHistoryData.length;
                resultsInfo.style.display = "block";
            } else {
                resultsInfo.style.display = "none";
            }
        }
    }

    /**
     * Highlight search terms in text
     */
    highlightSearchTerm(text, searchTerm) {
        if (!searchTerm || !text) return text;

        // Escape special regex characters in search term
        const escapedTerm = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

        // Create regex for case-insensitive search
        const regex = new RegExp(`(${escapedTerm})`, "gi");

        // Replace matches with highlighted version
        return text.replace(regex, '<span class="search-highlight">$1</span>');
    }

    /**
     * Check if the panel is currently visible
     */
    isVisible() {
        return (
            this.historyPanel && this.historyPanel.classList.contains("visible")
        );
    }

    /**
     * Toggle panel visibility
     */
    togglePanel() {
        if (this.isVisible()) {
            this.hidePanel();
        } else {
            this.showPanel();
        }
    }
}
